import type { ApolloError } from "@apollo/client";
import type { GraphQLFormattedError } from "graphql";

/**
 * Stable backend error codes this feature knows how to react to.
 * Sourced from `packages/nbs-auth/src/errors/auth-error.ts` and
 * `packages/nbs-graphql/src/errors/graphql-error.ts` — never invented here.
 */
export type ReviewErrorCode =
  | "UNAUTHENTICATED"
  | "TOKEN_EXPIRED"
  | "FORBIDDEN"
  | "PERMISSION_DENIED"
  | "UNKNOWN_ROLE"
  | "NOT_FOUND"
  | "FORBIDDEN_PROPOSAL_TRANSITION"
  | "MISSING_JUSTIFICATION"
  | "VALIDATION"
  | "CONCURRENT_MODIFICATION"
  | "QUERY_LIMIT_EXCEEDED"
  | "TIMEOUT"
  | "INTERNAL_ERROR"
  | "NETWORK_ERROR"
  | "UNKNOWN_ERROR";

export interface ReviewErrorViewModel {
  readonly code: ReviewErrorCode;
  readonly message: string;
  readonly correlationId: string | null;
  readonly retryable: boolean;
}

const KNOWN_CODES = new Set<ReviewErrorCode>([
  "UNAUTHENTICATED",
  "TOKEN_EXPIRED",
  "FORBIDDEN",
  "PERMISSION_DENIED",
  "UNKNOWN_ROLE",
  "NOT_FOUND",
  "FORBIDDEN_PROPOSAL_TRANSITION",
  "MISSING_JUSTIFICATION",
  "VALIDATION",
  "CONCURRENT_MODIFICATION",
  "QUERY_LIMIT_EXCEEDED",
  "TIMEOUT",
  "INTERNAL_ERROR",
]);

function normalizeCode(raw: unknown): ReviewErrorCode {
  if (typeof raw === "string" && KNOWN_CODES.has(raw as ReviewErrorCode)) {
    return raw as ReviewErrorCode;
  }
  return "UNKNOWN_ERROR";
}

function fromFormattedError(first: GraphQLFormattedError): ReviewErrorViewModel {
  const extensions = (first.extensions ?? {}) as Record<string, unknown>;
  return {
    code: normalizeCode(extensions["code"]),
    message: first.message,
    correlationId:
      typeof extensions["correlationId"] === "string" ? extensions["correlationId"] : null,
    retryable: extensions["retryable"] === true,
  };
}

/**
 * Maps an ApolloError onto a small, exhaustive view model.
 * Never reads stack traces, SQL, JWTs, or headers — only the sanitized
 * GraphQL error envelope the backend already produced.
 */
export function extractGraphQLError(
  error: ApolloError | undefined | null,
): ReviewErrorViewModel | null {
  if (!error) return null;

  const first = error.graphQLErrors?.[0];
  if (first) return fromFormattedError(first);

  if (error.networkError) {
    return {
      code: "NETWORK_ERROR",
      message: error.networkError.message,
      correlationId: null,
      retryable: true,
    };
  }

  return {
    code: "UNKNOWN_ERROR",
    message: error.message,
    correlationId: null,
    retryable: false,
  };
}

/**
 * With `errorPolicy: "all"` (this app's default for mutations), Apollo
 * resolves the mutation promise with `{ data, errors }` instead of
 * rejecting — so mutation error handling must inspect the resolved
 * `errors` array first, falling back to a thrown ApolloError only for
 * network-level failures.
 */
export function extractMutationError(
  formattedErrors: readonly GraphQLFormattedError[] | undefined,
  thrown: unknown,
): ReviewErrorViewModel | null {
  if (formattedErrors && formattedErrors.length > 0) {
    return fromFormattedError(formattedErrors[0] as GraphQLFormattedError);
  }
  if (thrown instanceof Error) {
    return extractGraphQLError(thrown as ApolloError);
  }
  return null;
}

export function isAuthErrorCode(code: ReviewErrorCode): boolean {
  return (
    code === "UNAUTHENTICATED" ||
    code === "TOKEN_EXPIRED" ||
    code === "FORBIDDEN" ||
    code === "PERMISSION_DENIED" ||
    code === "UNKNOWN_ROLE"
  );
}

export function isAlreadyTreatedError(code: ReviewErrorCode): boolean {
  return code === "FORBIDDEN_PROPOSAL_TRANSITION" || code === "CONCURRENT_MODIFICATION";
}
